import { useMutation, useQueryClient } from '@tanstack/react-query';
import { warrantyApi } from '../api/warrantyApi';
import type { CreateWarrantyDto, WarrantyListResponse } from '../types/warranty.types';

/**
 * Facettes des filtres de la liste : les valeurs réellement présentes en base
 * (types, durées, statuts, présence d'assistance).
 *
 * À purger avec les listes. Elles ont un `staleTime` de 60 s et aucune mutation
 * ne les invalidait : la première garantie d'une société à porter une
 * assistance — ou à être désactivée — doit faire apparaître immédiatement la
 * colonne et le filtre correspondants, pas à la minute suivante.
 *
 * Inutile en revanche après un réordonnancement ou une propagation du
 * diagnostic : ni l'ordre ni ce texte n'alimentent une facette.
 */
const WARRANTY_FACETS_KEY = ['warranty-filter-facets'];

export const useActivateWarranty = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => warrantyApi.activateWarranty(id),
    onSuccess: (data, id) => {
      queryClient.invalidateQueries({ queryKey: ['warranty', id] });
      queryClient.invalidateQueries({ queryKey: ['warranties'] });
      queryClient.invalidateQueries({ queryKey: WARRANTY_FACETS_KEY });
      queryClient.invalidateQueries({ queryKey: ['warranties-by-society', data.societyId] });
      queryClient.invalidateQueries({ queryKey: ['available-warranties', data.societyId] });
    },
  });
};

export const useDeactivateWarranty = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => warrantyApi.deactivateWarranty(id),
    onSuccess: (data, id) => {
      queryClient.invalidateQueries({ queryKey: ['warranty', id] });
      queryClient.invalidateQueries({ queryKey: ['warranties'] });
      queryClient.invalidateQueries({ queryKey: WARRANTY_FACETS_KEY });
      queryClient.invalidateQueries({ queryKey: ['warranties-by-society', data.societyId] });
      queryClient.invalidateQueries({ queryKey: ['available-warranties', data.societyId] });
    },
  });
};

export const useUpdateWarranty = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, warranty }: { id: string; warranty: Record<string, any> }) =>
      warrantyApi.updateWarranty(id, warranty),
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['warranty', id] });
      queryClient.invalidateQueries({ queryKey: ['warranty-pdf', id] });
      queryClient.invalidateQueries({ queryKey: ['warranties'] });
      queryClient.invalidateQueries({ queryKey: WARRANTY_FACETS_KEY });
      queryClient.invalidateQueries({ queryKey: ['warranties-by-society', data.societyId] });
      queryClient.invalidateQueries({ queryKey: ['available-warranties', data.societyId] });
    },
  });
};

/**
 * Propage les "Informations complémentaires" d'une garantie source vers
 * toutes les autres garanties de la même société.
 * Invalide la liste globale, la liste par société, et chaque garantie
 * individuellement (pour rafraîchir leur cache de détail).
 */
export const usePropagateWarrantyDiagnostic = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (uuid: string) => warrantyApi.propagateDiagnostic(uuid),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['warranties'] });
      queryClient.invalidateQueries({ queryKey: ['warranties-by-society', data.societyId] });
      queryClient.invalidateQueries({ queryKey: ['available-warranties', data.societyId] });
      data.updatedUuids.forEach((uuid) => {
        queryClient.invalidateQueries({ queryKey: ['warranty', uuid] });
        queryClient.invalidateQueries({ queryKey: ['warranty-pdf', uuid] });
      });
    },
  });
};

export interface MoveWarrantyVariables {
  id: string;
  targetUuid: string;
  position: 'before' | 'after';
}

/**
 * Déplace une garantie avant/après une de ses voisines.
 *
 * La mise à jour est optimiste : dnd-kit relâche sa transformation dès le
 * drop, donc sans elle la carte revient visuellement à sa place le temps de
 * l'aller-retour serveur — ce que l'utilisateur lit comme un échec. En cas
 * d'erreur on restaure le cache et on le dit, au lieu d'échouer en silence.
 */
export const useMoveWarranty = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, targetUuid, position }: MoveWarrantyVariables) =>
      warrantyApi.moveWarranty(id, targetUuid, position),

    onMutate: async ({ id, targetUuid, position }: MoveWarrantyVariables) => {
      await queryClient.cancelQueries({ queryKey: ['warranties'] });
      const snapshot = queryClient.getQueriesData<WarrantyListResponse>({ queryKey: ['warranties'] });

      // Même algorithme que l'API : retrait, puis réinsertion relative au
      // repère dont l'index est recalculé APRÈS retrait.
      //
      // Les gardes reproduisent celles de `move()` côté API. Toutes restent
      // DANS l'updater : sortir de `onMutate` en amont renverrait un contexte
      // sans `snapshot`, et `onError` échouerait en tentant de le restaurer.
      queryClient.setQueriesData<WarrantyListResponse>({ queryKey: ['warranties'] }, (previous) => {
        if (!previous?.warranties) return previous;

        // Déplacement sur soi-même : sans ce test, le repère disparaît avec le
        // retrait et `insertAt` vaut 0 ('after') ou -1 ('before') — la carte
        // sauterait en tête ou s'insérerait avant la dernière.
        if (id === targetUuid) return previous;

        const fromIndex = previous.warranties.findIndex((w) => w.uuid === id);
        if (fromIndex === -1) return previous;

        const warranties = [...previous.warranties];
        const [moved] = warranties.splice(fromIndex, 1);

        const targetIndexAfterRemoval = warranties.findIndex((w) => w.uuid === targetUuid);
        if (targetIndexAfterRemoval === -1) return previous;

        const insertAt =
          position === 'after' ? targetIndexAfterRemoval + 1 : targetIndexAfterRemoval;
        warranties.splice(insertAt, 0, moved);

        return { ...previous, warranties };
      });

      return { snapshot };
    },

    onError: (_error, _variables, context) => {
      context?.snapshot?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },

    onSettled: (data, _error, { id }: MoveWarrantyVariables) => {
      queryClient.invalidateQueries({ queryKey: ['warranty', id] });
      queryClient.invalidateQueries({ queryKey: ['warranties'] });
      if (data?.societyId) {
        queryClient.invalidateQueries({ queryKey: ['warranties-by-society', data.societyId] });
      }
      queryClient.invalidateQueries({ queryKey: ['available-warranties'] });
    },
  });
};

export const useDuplicateWarranty = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, newName }: { id: string; newName?: string }) =>
      warrantyApi.duplicateWarranty(id, newName),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['warranties'] });
      queryClient.invalidateQueries({ queryKey: WARRANTY_FACETS_KEY });
      queryClient.invalidateQueries({ queryKey: ['warranties-by-society', data.societyId] });
      queryClient.invalidateQueries({ queryKey: ['available-warranties', data.societyId] });
    },
  });
};

export const useCopyWarrantiesToSociety = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sourceSocietyId, targetSocietyId }: { sourceSocietyId: string; targetSocietyId: string }) =>
      warrantyApi.copyToSociety(sourceSocietyId, targetSocietyId),
    onSuccess: (_, { targetSocietyId }) => {
      queryClient.invalidateQueries({ queryKey: ['warranties'] });
      queryClient.invalidateQueries({ queryKey: WARRANTY_FACETS_KEY });
      queryClient.invalidateQueries({ queryKey: ['warranties-by-society', targetSocietyId] });
      queryClient.invalidateQueries({ queryKey: ['available-warranties', targetSocietyId] });
    },
  });
};

export const useCreateWarranty = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateWarrantyDto) => warrantyApi.createWarranty(dto),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['warranties'] });
      queryClient.invalidateQueries({ queryKey: WARRANTY_FACETS_KEY });
      if (data?.societyId) {
        queryClient.invalidateQueries({ queryKey: ['warranties-by-society', data.societyId] });
        queryClient.invalidateQueries({ queryKey: ['available-warranties', data.societyId] });
      }
    },
  });
};
